import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Table,
  Form,
  Modal,
} from "react-bootstrap";
import { FaPlus, FaTrash, FaEdit } from "react-icons/fa";
import "./CustomerList.css";
import axios from "axios";

const WorkingHours = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [show, setShow] = useState(false);
  const [designations, setDesignations] = useState([]);
  const [workingHrsList, setWorkingHrsList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Convert 24-hour time to 12-hour format with AM/PM
  const formatTo12Hour = (time24) => {
    if (!time24) return "";
    const [hhStr, mm] = time24.split(":");
    let hh = Number(hhStr);
    const ampm = hh >= 12 ? "PM" : "AM";
    hh = hh % 12;
    if (hh === 0) hh = 12;
    const hhPadded = hh.toString().padStart(2, "0");
    return `${hhPadded}:${mm} ${ampm}`;
  };

  // Convert 12-hour time to 24-hour format
  const formatTo24Hour = (time12) => {
    if (!time12) return "00:00";

    const time = time12.replace(/\s/g, ''); // Remove spaces
    const [timePart, ampm] = time.match(/([0-9]{1,2}:[0-9]{2})(AM|PM)?/i)?.slice(1) || [];

    if (!timePart) return "00:00";

    let [hh, mm] = timePart.split(':');
    hh = parseInt(hh, 10);

    if (ampm) {
      if (ampm.toUpperCase() === 'PM' && hh < 12) hh += 12;
      if (ampm.toUpperCase() === 'AM' && hh === 12) hh = 0;
    }

    return `${hh.toString().padStart(2, '0')}:${mm}`;
  };

  // Generate time options for 12-hour format
  const generateTimeOptions = () => {
    const times = [];

    // AM times
    for (let hour = 1; hour <= 11; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} AM`;
        times.push(timeString);
      }
    }

    // 12 PM
    times.push("12:00 PM");
    times.push("12:30 PM");

    // PM times
    for (let hour = 1; hour <= 11; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} PM`;
        times.push(timeString);
      }
    }

    return times;
  };

  const timeOptions = generateTimeOptions();

  // Fetch working hours and designations
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch working hours
      const workingHrsResponse = await axios.post(`${API_BASE_URL}working_hrs_list.php`);
      if (workingHrsResponse.data.head.code === 200) {
        // Filter out empty entries
        const filteredList = workingHrsResponse.data.body.filter(item => 
          item.working_id && 
          item.from_date && 
          item.to_date && 
          item.desig_id
        );
        setWorkingHrsList(filteredList);
      } else {
        console.error("Error Fetching Working Hours:", workingHrsResponse.data.head.msg);
        setWorkingHrsList([]);
      }

      // Fetch designations
      const designationResponse = await axios.post(`${API_BASE_URL}designation_list.php`);
      if (designationResponse.data.head.code === 200) {
        const list = designationResponse.data.body;
        setDesignations(list);
        if (list.length > 0) {
          setFormData(prev => ({
            ...prev,
            desig_id: list[0].desig_id
          }));
        }
      } else {
        console.error("Error Fetching Designations:", designationResponse.data.head.msg);
        setDesignations([]);
      }
    } catch (error) {
      console.error("API Error:", error);
      setWorkingHrsList([]);
      setDesignations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const [workingId, setWorkingId] = useState(null);

  const defaultForm = {
    working_id: null,  // Make sure this is null for add mode
    from_date: "",
    to_date: "",
    desig_id: "",
    from_time: "09:00 AM",
    to_time: "05:00 PM",
};

  const [formData, setFormData] = useState(defaultForm);

  const handleShow = () => {
    setWorkingId(null);  // Ensure workingId is null for add
    setFormData({
        working_id: null,
        from_date: "",
        to_date: "",
        desig_id: designations.length > 0 ? designations[0].desig_id : "",
        from_time: "09:00 AM",
        to_time: "05:00 PM",
    });
    setShow(true);
};

  const handleClose = () => {
    setWorkingId(null);  // Reset workingId
    setFormData({
        working_id: null,  // Reset working_id in form data
        from_date: "",
        to_date: "",
        desig_id: designations.length > 0 ? designations[0].desig_id : "",
        from_time: "09:00 AM",
        to_time: "05:00 PM",
    });
    setShow(false);
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get working_id from formData state
    const { from_date, to_date, desig_id, from_time, to_time, working_id } = formData;
    
    // Enhanced validation
    if (!from_date || !to_date || !desig_id || !from_time || !to_time) {
        alert("Please fill in all required fields!");
        return;
    }

    // Validate date range
    if (new Date(from_date) > new Date(to_date)) {
        alert("From Date cannot be later than To Date!");
        return;
    }

    // Validate time (from_time should be before to_time)
    const fromTimeObj = new Date(`1970-01-01 ${formatTo24Hour(from_time)}`);
    const toTimeObj = new Date(`1970-01-01 ${formatTo24Hour(to_time)}`);
    if (fromTimeObj >= toTimeObj) {
        alert("From Time must be before To Time!");
        return;
    }

    try {
        let data;
        let endpoint;

        if (workingId) {
            // EDIT MODE - Include working_id
            data = {
                working_id: workingId,  // This is the key field!
                from_date: from_date,
                to_date: to_date,
                desig_id: desig_id,
                from_time: formatTo24Hour(from_time),
                to_time: formatTo24Hour(to_time)
            };
            endpoint = `${API_BASE_URL}working_hrs_detail_edit.php`;
            console.log("Updating record with data:", data);
        } else {
            // ADD MODE
            data = {
                from_date: from_date,
                to_date: to_date,
                desig_id: desig_id,
                from_time: formatTo24Hour(from_time),
                to_time: formatTo24Hour(to_time)
            };
            endpoint = `${API_BASE_URL}working_hrs_add.php`;
            console.log("Adding new record with data:", data);
        }

        const response = await axios.post(endpoint, data, {
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (response.data.head.code === 200) {
            await fetchData(); // Refresh the list
            handleClose();
            alert(workingId ? "Record updated successfully!" : "Record added successfully!");
        } else {
            alert(response.data.head.msg || "Operation failed. Please try again.");
        }
    } catch (error) {
        console.error("API Error:", error);
        if (error.response && error.response.data) {
            alert(error.response.data.head?.msg || "Server error occurred");
        } else {
            alert("Network error. Please check your connection.");
        }
    }
};

  const handleEditItem = (item) => {
    console.log("Editing item:", item);
    console.log("Working ID from item:", item.working_id);
    
    const convertToInputDate = (dateStr) => {
        if (!dateStr) return "";
        // Check if date is already in YYYY-MM-DD format
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return dateStr;
        }
        // Convert from DD-MM-YYYY to YYYY-MM-DD
        const [dd, mm, yyyy] = dateStr.split("-");
        return `${yyyy}-${mm}-${dd}`;
    };

    // IMPORTANT: Set working_id in both states
    const updatedData = {
        working_id: item.working_id,  // Make sure this is set
        desig_id: item.desig_id || "",
        from_date: convertToInputDate(item.from_date),
        to_date: convertToInputDate(item.to_date),
        from_time: formatTo12Hour(item.from_time),
        to_time: formatTo12Hour(item.to_time),
    };

    console.log("Updated form data:", updatedData);
    
    // Set both the form data and the workingId state
    setFormData(updatedData);
    setWorkingId(item.working_id);  // This is crucial!
    setShow(true);
};

  const handleRemoveItem = async (working_id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) {
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}working_hrs_delete.php`, { working_id });
      if (response.data.head.code === 200) {
        await fetchData(); // Refresh data without reload
      } else {
        console.error("Error Deleting Data:", response.data.head.msg);
        alert("Failed to delete record. Please try again.");
      }
    } catch (error) {
      console.error("API Error:", error);
      alert("An error occurred. Please try again.");
    }
  };

  return (
    <>
      <Container fluid className="my-4 outer-container">
        <h2 className="text-center">🕒 Working Hours</h2>
        
        {loading && (
          <Container fluid className="outer-container my-4">
            <Row>
              <Col>
                <div className="not-found-box">Loading...</div>
              </Col>
            </Row>
          </Container>
        )}

        {!loading && (
          <>
            <Row className="mb-3">
              <Col className="d-flex justify-content-end">
                <Button className="btn-top" onClick={handleShow}>
                  <FaPlus /> Add
                </Button>
              </Col>
            </Row>

            <Table bordered responsive className="table-striped my-3 customer-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Date</th>
                  <th>Designation</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {workingHrsList.length > 0 ? (
                  workingHrsList.map((item, i) => (
                    <tr key={item.working_id}>
                      <td>{i + 1}</td>
                      <td>{item.from_date} to {item.to_date}</td>
                      <td>{item.desig_name || item.desig_id}</td>
                      <td>{formatTo12Hour(item.from_time)}</td>
                      <td>{formatTo12Hour(item.to_time)}</td>
                      <td className="">
                        <FaEdit
                          className="edit-icon me-3"
                          onClick={() => handleEditItem(item)}
                          style={{ cursor: "pointer", marginRight: "10px" }}
                        />
                        <FaTrash
                          className="delete-icon"
                          onClick={() => handleRemoveItem(item.working_id)}
                          style={{ cursor: "pointer" }}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No working hours records found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </>
        )}
      </Container>

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>{workingId ? "Edit Working Hours" : "Add Working Hours"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            {/* From Date */}
            <Form.Group className="mb-3">
              <Form.Label>From Date <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                readOnly={!!formData.working_id}
                value={formData.from_date}
                onChange={(e) =>
                  setFormData({ ...formData, from_date: e.target.value })
                }
                required
              />
            </Form.Group>

            {/* To Date */}
            <Form.Group className="mb-3">
              <Form.Label>To Date <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                value={formData.to_date}
                onChange={(e) =>
                  setFormData({ ...formData, to_date: e.target.value })
                }
                required
              />
            </Form.Group>

            {/* Designation */}
            <Form.Group className="mb-3">
              <Form.Label>Designation <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={formData.desig_id}
                disabled={!!formData.working_id}
                onChange={(e) =>
                  setFormData({ ...formData, desig_id: e.target.value })
                }
                required
              >
                <option value="">Select Designation</option>
                {designations.map((designation) => (
                  <option key={designation.desig_id} value={designation.desig_id}>
                    {designation.desig_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>From Time <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formData.from_time}
                    disabled={!!formData.working_id}
                    onChange={(e) => setFormData({ ...formData, from_time: e.target.value })}
                    required
                  >
                    {timeOptions.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>To Time <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formData.to_time}
                    disabled={!!formData.working_id}
                    onChange={(e) => setFormData({ ...formData, to_time: e.target.value })}
                    required
                  >
                    {timeOptions.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end">
              <Button onClick={handleClose} className="me-2 btn-top">
                Cancel
              </Button>
              <Button type="submit" className="btn-submit btn">
                {workingId ? "Update" : "Submit"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default WorkingHours;